/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

import { backgroundColorMap } from "../chart/edit";
import { getAccessibleLabel } from "../chart/save";

class GSSMultiChart {
    constructor(element) {
        this.charts = [];
        this.chartConfigs = [];
        this.labels = [];
        this.selectedDataset = 0;
        this.maxSliderWidth = 3 * window.innerWidth;
        this.grabElements(element);
        this.setupCharts();
        this.bindEvents();
    }

    grabElements(element) {
        this.wrapper = element;
        this.legend = this.wrapper.querySelector('.gss-multi-chart-legend');
        this.arrowsContainer = this.wrapper.querySelector('.gss-multichart__arrows');
        this.sliderContainer = this.arrowsContainer.nextSibling;
        this.arrowLeft = this.wrapper.querySelector('.gss-multichart__arrow--left');
        this.arrowRight = this.wrapper.querySelector('.gss-multichart__arrow--right');
        this.select = this.legend.querySelector('select');
        this.labelsContainer = this.legend.querySelector('.gss-multi-chart-labels');
        this.chartCanvases = this.wrapper.querySelectorAll('.gss-chartjs-multichart');
        this.chartsColumn = this.wrapper.querySelector('.gss-multi-chart__charts-column');
    }

    setupCharts() {
        this.chartCanvases.forEach(canvas => {
            const allChartsConfig = JSON.parse(canvas.dataset.chartjsConfig);
            this.chartConfigs.push(allChartsConfig);
            this.charts.push(new Chart(canvas, {...allChartsConfig[0]}));
        });
        this.labels = JSON.parse(this.legend.dataset.labels);
    }

    changeDataset(dataset) {
        this.selectedDataset = dataset;
        this.charts.forEach((chart, i) => {
            const chartData = this.chartConfigs[i][dataset].data;
            const chartCanvas = this.chartCanvases[i];
            if (chartData.datasets[0].data.length) {
                chart.data = chartData;
                chart.update();
                // Update accessible label for each chart using getAccessibleLabel from ../chart/save.js                
                const labelDiv = chartCanvas.nextSibling;                
                if (labelDiv) {
                    // Try to get chart config and relevant info
                    const chartConfig = this.chartConfigs[i][dataset];
                    const datasetName = chartConfig.data?.datasets?.[0]?.label || '';
                    const chartTitle = chartConfig.options?.plugins?.title?.text + ' - ' + datasetName  || '';
                    const xAxis = chartConfig.options?.scales?.x?.title?.text || '';
                    const yAxis = chartConfig.options?.scales?.y?.title?.text || '';
                    const labelsArr = this.labels[dataset]?.labels || [];
                    const datasetData = chartConfig.data?.datasets?.[0]?.data || [];                

                    // Build attributes object as expected by getAccessibleLabel
                    const attributes = {
                        chartType: chartConfig.type,
                        chartTitle: chartTitle,
                        xAxis: xAxis,
                        yAxis: yAxis,
                        labels: labelsArr,
                        datasets: [{
                            name: '',
                            data: datasetData
                        }]
                    };

                    labelDiv.textContent = getAccessibleLabel(attributes);
                }

                chartCanvas.parentNode.classList.remove('hidden');
            } else {
                chartCanvas.parentNode.classList.add('hidden');
            }
        });
        this.labelsContainer.innerHTML = '';
        this.labels[dataset].labels.forEach((label, i) => {
            this.labelsContainer.innerHTML += `<p><span style="background-color: ${backgroundColorMap[i % 7]}"  class="gss-multi-chart-legend__color-box"></span>${label}</p>`
        });
        this.getSliderWidth();
        this.handleResize();
    }

    nextChart() {        
        if (!this.chartsColumn) return;
        // Get current transform value
        const style = window.getComputedStyle(this.chartsColumn);
        const matrix = style.transform !== "none" ? style.transform : "";
        let currentTranslate = 0;
        if (matrix && matrix.startsWith("matrix")) {
            // matrix(1, 0, 0, 1, tx, ty)
            const values = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');
            currentTranslate = parseFloat(values[4]) || 0;
        }
        // Calculate new translate value
        const chartCount = this.charts.length;
        const vw = window.innerWidth;
        let newTranslate = currentTranslate * -1 + vw;
        // Do not exceed (chartCount - 1) * 100vw
        const maxTranslate = this.maxSliderWidth;
        if (newTranslate > maxTranslate) {
            newTranslate = maxTranslate;
        }
        this.chartsColumn.style.transform = `translateX(-${newTranslate}px)`;

    }

    previousChart() {
        if (!this.chartsColumn) return;
        // Get current transform value
        const style = window.getComputedStyle(this.chartsColumn);
        const matrix = style.transform !== "none" ? style.transform : "";
        let currentTranslate = 0;
        if (matrix && matrix.startsWith("matrix")) {
            // matrix(1, 0, 0, 1, tx, ty)
            const values = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');
            currentTranslate = parseFloat(values[4]) || 0;
        }
        // Calculate new translate value
        const vw = window.innerWidth;
        let newTranslate = currentTranslate * -1 - vw;
        // Do not go below 0
        if (newTranslate < 0) {
            newTranslate = 0;
        }
        this.chartsColumn.style.transform = `translateX(-${newTranslate}px)`;
    }

    handleResize() {
        if (this.chartsColumn) {
            if (this._resizeTimeout) {
                clearTimeout(this._resizeTimeout);
            }
            this._resizeTimeout = setTimeout(() => {
                if (this.chartsColumn.style.transform !== 'translateX(0)' && this.chartsColumn.style.transform !== '') {
                    this.chartsColumn.style.transform = 'translateX(0)';
                }
                this.getSliderWidth();
            }, 150);
        }
    };

    getSliderWidth() {
        if (window.matchMedia('(max-width: 781px)').matches) {
            let totalVisibleCanvases = 0;
            this.chartCanvases.forEach((canvas) => {
                const isHidden = canvas.parentNode.classList.contains('hidden');
                if (!isHidden) {
                    totalVisibleCanvases++;
                }
            }, 0);
    
            this.maxSliderWidth = (totalVisibleCanvases - 1) * window.innerWidth;
        }
    }

    bindEvents() {
        this.select.addEventListener('change', (e) => {
            this.changeDataset(parseInt(e.target.value));
        });
        this.arrowRight.addEventListener('click', this.nextChart.bind(this));
        this.arrowLeft.addEventListener('click', this.previousChart.bind(this));
        window.addEventListener('resize', this.handleResize.bind(this));
    }
}

document.querySelectorAll('.wp-block-gss-multi-chart').forEach(multichart => {
    new GSSMultiChart(multichart);
});
